import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Clock, Users } from 'lucide-react';
import { GameDetailsCard } from '@/components/games/GameDetailsCard';
import { useUserRole } from '@/hooks/useUserRole';
import { queueActionService, QueueActionResult } from '@/services/queueActionService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const GameActionHandler: React.FC = () => {
  const { gameId, gameSlug, actionType } = useParams<{ gameId?: string; gameSlug?: string; actionType?: string }>();
  const finalGameId = gameId || gameSlug;
  const finalActionType = actionType || 'approval';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { permissions, userId } = useUserRole();
  
  const [gameData, setGameData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [applicationId] = useState(searchParams.get('applicationId'));

  useEffect(() => {
    if (finalGameId) {
      loadGameData();
    }
  }, [finalGameId]);

  const loadGameData = async () => {
    try {
      // Try venue games first
      const { data: venueGame } = await supabase
        .from('venue_games')
        .select(`
          *,
          sport:available_sports(sport_name),
          home_team:teams!venue_games_home_team_id_fkey(team_id, team_name, team_logo),
          away_team:teams!venue_games_away_team_id_fkey(team_id, team_name, team_logo)
        `)
        .eq('id', finalGameId)
        .maybeSingle();

      if (venueGame) {
        setGameData({ ...venueGame, source: 'venue' });
        setLoading(false);
        return;
      }

      // Try regular games
      const { data: regularGame } = await supabase
        .from('games')
        .select(`
          *,
          home_team:teams!games_home_team_id_fkey(team_id, team_name, team_logo),
          away_team:teams!games_away_team_id_fkey(team_id, team_name, team_logo),
          sport:available_sports(sport_name)
        `)
        .eq('id', finalGameId)
        .maybeSingle();

      if (regularGame) {
        setGameData({ ...regularGame, source: 'regular' });
      }
    } catch (error) {
      console.error('Error loading game data:', error);
      toast.error('Failed to load game data');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: 'approve' | 'reject' | 'confirm' | 'apply') => {
    if (!userId || !finalGameId) return;

    setActionLoading(true);
    try {
      let result: QueueActionResult;

      switch (finalActionType) {
        case 'approval':
          result = await queueActionService.handleGameApproval(finalGameId, action as 'approve' | 'reject', userId, 'supabase', reason);
          break;
        
        case 'scorekeeper-application':
          if (action === 'apply') {
            result = await queueActionService.handleScorekeeperApplication(finalGameId, 'apply', userId, 'supabase');
          } else if (applicationId) {
            result = await queueActionService.handleScorekeeperApplication(finalGameId, action as 'approve' | 'reject', userId, 'supabase', applicationId);
          } else {
            result = { success: false, message: 'Missing application ID' };
          }
          break;
        
        case 'confirmation':
          result = await queueActionService.handleTeamConfirmation(finalGameId, userId, 'supabase');
          break;
        
        default:
          result = { success: false, message: 'Unknown action type' };
      }

      if (result.success) {
        toast.success(result.message);
        if (result.redirect) {
          navigate(result.redirect);
        } else {
          navigate('/game-management');
        }
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error handling action:', error);
      toast.error('Failed to process action');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Clock className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading game details...</p>
        </div>
      </div>
    );
  }

  if (!gameData) {
    return (
      <div className="container mx-auto p-6">
        <Card className="bg-destructive/10 border-destructive">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-destructive mb-2">Game Not Found</h2>
            <p className="text-muted-foreground">The requested game could not be found.</p>
            <Button 
              onClick={() => navigate('/game-management')} 
              className="mt-4"
              variant="outline"
            >
              Back to Game Management
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getActionTitle = () => {
    switch (finalActionType) {
      case 'approval':
        return 'Game Approval Required';
      case 'scorekeeper-application':
        return applicationId ? 'Review Scorekeeper Application' : 'Apply as Scorekeeper';
      case 'confirmation':
        return 'Team Confirmation Required';
      default:
        return 'Game Action Required';
    }
  };

  const getActionDescription = () => {
    const gameName = gameData.game_title || gameData.game_name || 'Game';
    const gameDate = (() => {
      try {
        const { formatGameDateTime } = require('@/utils/timezone');
        const formatted = formatGameDateTime(
          gameData.game_date,
          gameData.game_time,
          gameData.game_timezone || 'UTC'
        );
        return formatted.date;
      } catch (error) {
        return format(new Date(gameData.game_date), 'MMMM dd, yyyy');
      }
    })();
    
    switch (finalActionType) {
      case 'approval':
        return `Review and approve the game "${gameName}" scheduled for ${gameDate}.`;
      case 'scorekeeper-application':
        return applicationId 
          ? `Review the scorekeeper application for "${gameName}" on ${gameDate}.`
          : `Apply to be the scorekeeper for "${gameName}" on ${gameDate}.`;
      case 'confirmation':
        return `Confirm your team's participation in "${gameName}" on ${gameDate}.`;
      default:
        return `Take action on "${gameName}" scheduled for ${gameDate}.`;
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Card className="bg-card border-border">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-foreground text-xl">
                {getActionTitle()}
              </CardTitle>
              <p className="text-muted-foreground text-sm mt-1">
                Action required for game management
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Game Information */}
          <GameDetailsCard 
            gameId={finalGameId!} 
            gameData={gameData} 
            showVenueInfo={true} 
          />

          {/* Action Description */}
          <div>
            <h3 className="font-semibold text-foreground mb-2">Action Required</h3>
            <p className="text-muted-foreground">{getActionDescription()}</p>
          </div>

          {/* Reason field for rejection */}
          {finalActionType === 'approval' && (
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Reason (required for rejection)
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide a reason for your decision..."
                className="min-h-[100px]"
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => navigate('/game-management')}
              disabled={actionLoading}
            >
              Cancel
            </Button>

            {/* Update Game Information Button */}
            {finalActionType === 'approval' && gameData && (
              (() => {
                const gameDateTime = new Date(`${gameData.game_date} ${gameData.game_time || '00:00'}`);
                const now = new Date();
                const isFutureGame = gameDateTime > now;
                
                return isFutureGame ? (
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/games/${finalGameId}/unified-form`)}
                    disabled={actionLoading}
                    className="flex items-center gap-2"
                  >
                    <Clock className="h-4 w-4" />
                    Update Game Information
                  </Button>
                ) : null;
              })()
            )}

            {finalActionType === 'approval' && gameData && (
              (() => {
                const gameDateTime = new Date(`${gameData.game_date} ${gameData.game_time || '00:00'}`);
                const now = new Date();
                const isFutureGame = gameDateTime > now;
                
                return isFutureGame ? (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => handleAction('reject')}
                      disabled={actionLoading}
                      className="flex items-center gap-2"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      Reject Game
                    </Button>
                    <Button
                      onClick={() => handleAction('approve')}
                      disabled={actionLoading}
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Approve Game
                    </Button>
                  </>
                ) : (
                  <div className="text-center text-muted-foreground text-sm">
                    This game has passed its scheduled time and cannot be approved or rejected.
                  </div>
                );
              })()
            )}

            {finalActionType === 'scorekeeper-application' && !applicationId && (
              <Button
                onClick={() => handleAction('apply')}
                disabled={actionLoading}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Apply as Scorekeeper
              </Button>
            )}

            {finalActionType === 'scorekeeper-application' && applicationId && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => handleAction('reject')}
                  disabled={actionLoading}
                  className="flex items-center gap-2"
                >
                  <AlertTriangle className="h-4 w-4" />
                  Reject Application
                </Button>
                <Button
                  onClick={() => handleAction('approve')}
                  disabled={actionLoading}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4" />
                  Approve Application
                </Button>
              </>
            )}

            {finalActionType === 'confirmation' && (
              <Button
                onClick={() => handleAction('confirm')}
                disabled={actionLoading}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Confirm Team Participation
              </Button>
            )}
          </div>

          {actionLoading && (
            <div className="text-center py-4">
              <Clock className="h-6 w-6 animate-spin mx-auto text-primary" />
              <p className="text-sm text-muted-foreground mt-2">Processing action...</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};